import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, queuedMessage } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, ClipboardCheck, Lock, RotateCcw, CheckCircle2, AlertTriangle, ArrowLeft, UserPlus } from "lucide-react";
import { toast, Toaster } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);

function statusPill(s) {
  if (s === "verified") return "bg-emerald-100 text-emerald-700";
  if (s === "closed") return "bg-amber-100 text-amber-700";
  return "bg-blue-100 text-blue-700";
}

export default function Audit() {
  const { user, isSuper } = useAuth();
  const role = user?.role;
  const isAdmin = isSuper || role === "admin";
  const isAuditor = role === "auditor";
  const canManage = isAdmin || isAuditor;

  if (!canManage) return <OperatorVariances />;
  return <AuditManager isAdmin={isAdmin} />;
}

function AuditManager({ isAdmin }) {
  const [audits, setAudits] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ type: "inventory", audit_date: todayStr(), notes: "" });
  const [myVar, setMyVar] = useState([]);
  const [myDrafts, setMyDrafts] = useState({});

  const load = useCallback(() => api.get("/audit").then((r) => setAudits(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  const loadMyVar = useCallback(() => api.get("/audit/my-variances").then((r) => setMyVar(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadMyVar(); }, [load, loadMyVar]);

  const submitMyVar = async (lineId) => {
    const text = (myDrafts[lineId] || "").trim();
    if (!text) { toast.error("Enter a justification"); return; }
    try { await api.post(`/audit/lines/${lineId}/justify`, { justification: text }); toast.success("Justification submitted"); loadMyVar(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const createAudit = async () => {
    try {
      const res = await api.post("/audit", newForm);
      toast.success("Audit started"); setShowNew(false); setSelected(res.data.id); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (selected) return <AuditDetail auditId={selected} isAdmin={isAdmin} onBack={() => { setSelected(null); load(); }} />;

  return (
    <Layout title="Audit" subtitle="Physical stock verification · inventory & process"
      actions={<button data-testid="audit-new-btn" onClick={() => setShowNew(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><Plus size={16} /> New Audit</button>}>
      <Toaster richColors position="top-right" />
      {myVar.length > 0 && (
        <div className="section-card mb-5 border-l-4 border-amber-400" data-testid="my-variance-panel">
          <h3 className="font-heading font-semibold text-slate-900 mb-2">Variances assigned to you to justify</h3>
          <div className="space-y-2">
            {myVar.map((l) => (
              <div key={l.id} className="flex items-center gap-3 flex-wrap text-sm">
                <span className="font-medium min-w-[160px]">{l.name}</span>
                <span className="text-slate-500">diff <b className={l.difference === 0 ? "text-emerald-600" : "text-red-600"}>{l.difference}</b></span>
                {l.my_justification ? <span className="text-emerald-700 text-xs">✓ {l.my_justification}</span> : (
                  <>
                    <input data-testid={`myvar-input-${l.id}`} value={myDrafts[l.id] ?? ""} onChange={(e) => setMyDrafts({ ...myDrafts, [l.id]: e.target.value })} className="flex-1 min-w-[200px] border border-slate-300 rounded-lg px-3 py-1.5" placeholder="Explain the difference" />
                    <button data-testid={`myvar-submit-${l.id}`} onClick={() => submitMyVar(l.id)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold">Submit</button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="section-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr><th className="text-left py-3 px-4">Date</th><th className="text-left py-3 px-4">Type</th><th className="text-left py-3 px-4">Status</th><th className="text-left py-3 px-4">By</th><th className="text-right py-3 px-4">Total Diff</th><th></th></tr>
          </thead>
          <tbody data-testid="audit-list-body">
            {audits.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-slate-400"><ClipboardCheck size={32} className="mx-auto mb-2 opacity-40" />No audits yet.</td></tr>}
            {audits.map((a) => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="py-2.5 px-4 font-medium">{a.audit_date}</td>
                <td className="py-2.5 px-4 capitalize">{a.type}</td>
                <td className="py-2.5 px-4"><span className={`stat-pill ${statusPill(a.status)}`}>{a.status}{a.reconciled ? " · reconciled" : ""}</span></td>
                <td className="py-2.5 px-4 text-slate-500 text-xs">{a.created_by}</td>
                <td className="py-2.5 px-4 text-right font-mono">{a.total_difference == null ? "—" : a.total_difference}</td>
                <td className="py-2.5 px-4 text-right"><button data-testid={`audit-open-${a.id}`} onClick={() => setSelected(a.id)} className="text-blue-600 hover:underline text-sm font-medium">Open</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b"><h2 className="font-heading text-xl font-semibold">New Audit</h2><button onClick={() => setShowNew(false)} className="text-slate-400"><X size={20} /></button></div>
            <div className="p-5 space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Audit Type</label>
                <select data-testid="audit-new-type" value={newForm.type} onChange={(e) => setNewForm({ ...newForm, type: e.target.value })} className="inp">
                  <option value="inventory">Inventory (item-wise, category)</option>
                  <option value="process">Process Stock (DPR process-wise)</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Audit Date</label><input data-testid="audit-new-date" type="date" value={newForm.audit_date} max={todayStr()} onChange={(e) => setNewForm({ ...newForm, audit_date: e.target.value })} className="inp" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Notes</label><input data-testid="audit-new-notes" value={newForm.notes} onChange={(e) => setNewForm({ ...newForm, notes: e.target.value })} className="inp" /></div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">Enter physical (ground-reality) stock. The system stock & difference are hidden until you Close the audit.</div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t bg-slate-50"><button onClick={() => setShowNew(false)} className="px-4 py-2 border rounded-lg text-sm bg-white">Cancel</button><button data-testid="audit-new-save" onClick={createAudit} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Start Audit</button></div>
          </div>
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #cbd5e1;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem}`}</style>
    </Layout>
  );
}

function AuditDetail({ auditId, isAdmin, onBack }) {
  const [audit, setAudit] = useState(null);
  const [rows, setRows] = useState([]);
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState({}); // ref_id -> physical value
  const [operators, setOperators] = useState([]);
  const [showVerify, setShowVerify] = useState(false);
  const [verifyForm, setVerifyForm] = useState({ reconcile: false, remark: "" });

  const loadAudit = useCallback(() => api.get(`/audit/${auditId}`).then((r) => setAudit(r.data)), [auditId]);
  const loadRows = useCallback(() => api.get(`/audit/${auditId}/items`, { params: category ? { category } : {} }).then((r) => {
    setRows(r.data);
    setEntries((prev) => { const m = { ...prev }; r.data.forEach((x) => { if (x.physical_qty != null && m[x.ref_id] === undefined) m[x.ref_id] = x.physical_qty; }); return m; });
  }), [auditId, category]);

  useEffect(() => { loadAudit(); }, [loadAudit]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); api.get("/audit/operators").then((r) => setOperators(r.data)).catch(() => {}); }, []);

  const isOpen = audit?.status === "open";
  const isClosed = audit?.status === "closed";
  const isVerified = audit?.status === "verified";

  const saveLines = async () => {
    const lines = Object.entries(entries).filter(([, v]) => v !== "" && v != null).map(([ref_id, v]) => ({ ref_id, physical_qty: +v }));
    if (lines.length === 0) { toast.error("Enter at least one quantity"); return; }
    try { await api.post(`/audit/${auditId}/lines`, { lines }); toast.success(`Saved ${lines.length} entries`); loadRows(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const closeAudit = async () => {
    try { const res = await api.post(`/audit/${auditId}/close`); setAudit(res.data); toast.success("Audit closed — differences revealed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const requestReopen = async () => {
    try { const res = await api.post(`/audit/${auditId}/reopen-request`); const q = queuedMessage(res); toast[q ? "info" : "success"](q || "Reopened"); loadAudit(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const verify = async () => {
    try { const res = await api.post(`/audit/${auditId}/verify`, verifyForm); setAudit(res.data); setShowVerify(false); toast.success(verifyForm.reconcile ? "Verified & stock reconciled" : "Verified (report only)"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [assignLine, setAssignLine] = useState(null);
  const [assignSel, setAssignSel] = useState([]);

  const openAssign = (l) => { setAssignLine(l); setAssignSel(Array.isArray(l.assigned_to) ? l.assigned_to : (l.assigned_to ? [l.assigned_to] : [])); };
  const toggleAssignee = (email) => setAssignSel((prev) => prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]);
  const saveAssign = async () => {
    try { await api.post(`/audit/lines/${assignLine.id}/assign`, { emails: assignSel }); toast.success("Assigned & notified"); setAssignLine(null); loadAudit(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const lines = audit?.lines || [];

  return (
    <Layout title="Audit Detail" subtitle={audit ? `${audit.type} · ${audit.audit_date}` : ""}
      actions={<button data-testid="audit-back-btn" onClick={onBack} className="bg-white border border-slate-300 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 flex items-center gap-2"><ArrowLeft size={16} /> Back</button>}>
      <Toaster richColors position="top-right" />
      {!audit ? <div className="text-sm text-slate-500">Loading…</div> : (
        <div className="space-y-5">
          <div className="section-card flex flex-wrap items-center gap-4">
            <span className={`stat-pill ${statusPill(audit.status)}`} data-testid="audit-status">{audit.status}{audit.reconciled ? " · reconciled" : ""}</span>
            <span className="text-sm text-slate-500">Type: <b className="capitalize text-slate-700">{audit.type}</b></span>
            <span className="text-sm text-slate-500">Date: <b className="text-slate-700">{audit.audit_date}</b></span>
            {audit.total_difference != null && <span className="text-sm text-slate-500">Total Difference: <b className={audit.total_difference === 0 ? "text-emerald-600" : "text-red-600"}>{audit.total_difference}</b></span>}
            <div className="ml-auto flex gap-2">
              {isOpen && <button data-testid="audit-close-btn" onClick={closeAudit} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><Lock size={15} /> Close Audit</button>}
              {isClosed && <button data-testid="audit-reopen-btn" onClick={requestReopen} className="bg-white border border-slate-300 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 flex items-center gap-2"><RotateCcw size={15} /> {isAdmin ? "Reopen" : "Request Reopen"}</button>}
              {isClosed && isAdmin && <button data-testid="audit-verify-btn" onClick={() => setShowVerify(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><CheckCircle2 size={15} /> Verify</button>}
            </div>
          </div>

          {isOpen && (
            <div className="section-card">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {audit.type === "inventory" && (
                  <>
                    <span className="text-sm font-semibold text-slate-700">Category:</span>
                    <select data-testid="audit-category" value={category} onChange={(e) => setCategory(e.target.value)} className="inp w-56">
                      <option value="">— All categories —</option>
                      {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </>
                )}
                <button data-testid="audit-save-lines" onClick={saveLines} className="ml-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">Save Entered Quantities</button>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 mb-3 flex items-center gap-2"><AlertTriangle size={14} /> Blind count: system stock is hidden. Enter physical quantity, save, then Close to reveal differences.</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider"><tr><th className="text-left py-2.5 px-4">Item / Process</th><th className="text-left py-2.5 px-4">Category</th><th className="text-left py-2.5 px-4">Unit</th><th className="text-right py-2.5 px-4">Physical Qty</th></tr></thead>
                  <tbody data-testid="audit-entry-body">
                    {rows.length === 0 && <tr><td colSpan="4" className="text-center py-8 text-slate-400">No items in this category.</td></tr>}
                    {rows.map((r) => (
                      <tr key={r.ref_id} className="border-t border-slate-100">
                        <td className="py-2 px-4 font-medium">{r.name}</td>
                        <td className="py-2 px-4 text-slate-500">{r.category}</td>
                        <td className="py-2 px-4 text-slate-500">{r.unit}</td>
                        <td className="py-2 px-4 text-right"><input data-testid={`audit-qty-${r.ref_id}`} type="number" value={entries[r.ref_id] ?? ""} onChange={(e) => setEntries({ ...entries, [r.ref_id]: e.target.value })} className="inp w-32 text-right" placeholder="0" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(isClosed || isVerified) && (
            <div className="section-card">
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Stock Difference {isVerified && audit.reconciled ? "(reconciled to physical)" : ""}</h3>
              {isVerified && audit.verify_remark && <div className="text-sm text-slate-500 mb-3">Admin remark: <b className="text-slate-700">{audit.verify_remark}</b></div>}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider"><tr><th className="text-left py-2.5 px-4">Item / Process</th><th className="text-right py-2.5 px-4">System</th><th className="text-right py-2.5 px-4">Physical</th><th className="text-right py-2.5 px-4">Difference</th><th className="text-left py-2.5 px-4">Justifications</th>{isClosed && <th className="text-left py-2.5 px-4">Assign Users</th>}</tr></thead>
                  <tbody data-testid="audit-diff-body">
                    {lines.map((l) => {
                      const assignedArr = Array.isArray(l.assigned_to) ? l.assigned_to : (l.assigned_to ? [l.assigned_to] : []);
                      const justs = (l.justifications && l.justifications.length) ? l.justifications : (l.justification ? [{ email: assignedArr[0] || "", text: l.justification }] : []);
                      const pending = assignedArr.filter((e) => !justs.find((j) => j.email === e));
                      return (
                      <tr key={l.id} className={`border-t border-slate-100 ${l.difference !== 0 ? "bg-red-50/40" : ""}`}>
                        <td className="py-2 px-4 font-medium">{l.name}</td>
                        <td className="py-2 px-4 text-right font-mono">{l.system_qty}</td>
                        <td className="py-2 px-4 text-right font-mono">{l.physical_qty}</td>
                        <td className={`py-2 px-4 text-right font-mono font-semibold ${l.difference === 0 ? "text-emerald-600" : "text-red-600"}`}>{l.difference}</td>
                        <td className="py-2 px-4 text-slate-600 text-xs max-w-xs">
                          {justs.map((j, k) => <div key={k} className="mb-0.5"><span className="text-slate-400">{j.email}:</span> <span className="text-slate-700">{j.text}</span></div>)}
                          {pending.map((e) => <div key={e} className="text-amber-600">awaiting {e}</div>)}
                          {justs.length === 0 && pending.length === 0 && "—"}
                        </td>
                        {isClosed && (
                          <td className="py-2 px-4">
                            <div className="flex flex-wrap gap-1 mb-1">{assignedArr.map((e) => <span key={e} className="text-[11px] bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">{e}</span>)}</div>
                            <button data-testid={`audit-assign-${l.id}`} onClick={() => openAssign(l)} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline"><UserPlus size={13} /> {assignedArr.length ? "Edit assignees" : "Assign users"}</button>
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {showVerify && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b"><h2 className="font-heading text-xl font-semibold">Verify Audit</h2><button onClick={() => setShowVerify(false)} className="text-slate-400"><X size={20} /></button></div>
            <div className="p-5 space-y-4">
              <label className="flex items-center gap-2 text-sm"><input data-testid="audit-reconcile-check" type="checkbox" checked={verifyForm.reconcile} onChange={(e) => setVerifyForm({ ...verifyForm, reconcile: e.target.checked })} /> Reconcile: adjust system stock to match the physical count (write off the difference)</label>
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Remark</label><textarea data-testid="audit-verify-remark" value={verifyForm.remark} onChange={(e) => setVerifyForm({ ...verifyForm, remark: e.target.value })} rows={2} className="inp" /></div>
              {verifyForm.reconcile && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">This will permanently adjust inventory/process stock to the audited physical quantities.</div>}
            </div>
            <div className="flex justify-end gap-2 p-5 border-t bg-slate-50"><button onClick={() => setShowVerify(false)} className="px-4 py-2 border rounded-lg text-sm bg-white">Cancel</button><button data-testid="audit-verify-save" onClick={verify} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold">Confirm Verify</button></div>
          </div>
        </div>
      )}
      {assignLine && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="audit-assign-modal">
            <div className="flex items-center justify-between p-5 border-b"><h2 className="font-heading text-xl font-semibold">Assign users to justify</h2><button onClick={() => setAssignLine(null)} className="text-slate-400"><X size={20} /></button></div>
            <div className="p-5">
              <p className="text-sm text-slate-500 mb-3">Select one or more users (any role) to verify & justify the difference for <b className="text-slate-700">{assignLine.name}</b> (diff {assignLine.difference}). Each gets a notification.</p>
              <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                {operators.map((o) => (
                  <label key={o.email} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                    <input data-testid={`assign-check-${o.email}`} type="checkbox" checked={assignSel.includes(o.email)} onChange={() => toggleAssignee(o.email)} />
                    <span className="flex-1">{o.name || o.email} <span className="text-xs text-slate-400">{o.email}</span></span>
                    <span className="text-[11px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 capitalize">{o.role}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t bg-slate-50"><button onClick={() => setAssignLine(null)} className="px-4 py-2 border rounded-lg text-sm bg-white">Cancel</button><button data-testid="audit-assign-save" onClick={saveAssign} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Save ({assignSel.length})</button></div>
          </div>
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #cbd5e1;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem}`}</style>
    </Layout>
  );
}

function OperatorVariances() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const load = useCallback(() => api.get("/audit/my-variances").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (lineId) => {
    const text = (drafts[lineId] || "").trim();
    if (!text) { toast.error("Enter a justification"); return; }
    try { await api.post(`/audit/lines/${lineId}/justify`, { justification: text }); toast.success("Justification submitted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout title="My Variance Justifications" subtitle="Stock differences assigned to you to verify & justify">
      <Toaster richColors position="top-right" />
      <div className="section-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider"><tr><th className="text-left py-3 px-4">Item / Process</th><th className="text-right py-3 px-4">System</th><th className="text-right py-3 px-4">Physical</th><th className="text-right py-3 px-4">Difference</th><th className="text-left py-3 px-4">Your Justification</th><th></th></tr></thead>
          <tbody data-testid="audit-variance-body">
            {rows.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-slate-400"><UserPlus size={32} className="mx-auto mb-2 opacity-40" />No variances assigned to you.</td></tr>}
            {rows.map((l) => (
              <tr key={l.id} className="border-t border-slate-100">
                <td className="py-2.5 px-4 font-medium">{l.name}</td>
                <td className="py-2.5 px-4 text-right font-mono">{l.system_qty}</td>
                <td className="py-2.5 px-4 text-right font-mono">{l.physical_qty}</td>
                <td className={`py-2.5 px-4 text-right font-mono font-semibold ${l.difference === 0 ? "text-emerald-600" : "text-red-600"}`}>{l.difference}</td>
                <td className="py-2.5 px-4">
                  {l.my_justification ? <span className="text-emerald-700 text-xs">✓ {l.my_justification}</span> :
                    <input data-testid={`variance-input-${l.id}`} value={drafts[l.id] ?? ""} onChange={(e) => setDrafts({ ...drafts, [l.id]: e.target.value })} className="inp" placeholder="Explain the difference" />}
                </td>
                <td className="py-2.5 px-4 text-right">{!l.my_justification && <button data-testid={`variance-submit-${l.id}`} onClick={() => submit(l.id)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold">Submit</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <style>{`.inp{width:100%;border:1px solid #cbd5e1;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem}`}</style>
    </Layout>
  );
}
